import React, { useState } from 'react';
import ToolLayout from '../components/ToolLayout';
import QRCode from 'qrcode';

const QrGenerator: React.FC = () => {
  const [input, setInput] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [options, setOptions] = useState({
    errorCorrectionLevel: 'M' as 'L' | 'M' | 'Q' | 'H',
    type: 'image/png' as 'image/png' | 'image/jpeg',
    quality: 0.92,
    margin: 1,
    color: {
      dark: '#000000',
      light: '#FFFFFF'
    },
    width: 256
  });

  const generateQR = async () => {
    setError('');
    setSuccess('');
    setQrCodeUrl('');

    if (!input.trim()) {
      setError('Vui lòng nhập text hoặc URL để tạo QR code');
      return;
    }

    try {
      const qrOptions = {
        errorCorrectionLevel: options.errorCorrectionLevel,
        type: options.type,
        quality: options.quality,
        margin: options.margin,
        color: options.color,
        width: options.width
      };

      const url = await QRCode.toDataURL(input, qrOptions);
      setQrCodeUrl(url);
      setSuccess('QR Code đã được tạo thành công! 📱');
    } catch (err) {
      setError('Không thể tạo QR code: ' + (err as Error).message);
      setQrCodeUrl('');
    }
  };

  const clearAll = () => {
    setInput('');
    setQrCodeUrl('');
    setError('');
    setSuccess('');
  };

  const downloadQR = () => {
    if (!qrCodeUrl) {return;}

    const link = document.createElement('a');
    link.download = 'qrcode.' + (options.type === 'image/png' ? 'png' : 'jpg');
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSuccess('QR Code đã được download! 💾');
  };

  const copyToClipboard = async () => {
    if (!qrCodeUrl) {return;}

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = async () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        
        canvas.toBlob(async (blob) => {
          if (blob) {
            try {
              await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob })
              ]);
              setSuccess('QR Code đã được copy vào clipboard! 📋');
            } catch (err) {
              setError('Không thể copy image vào clipboard');
            }
          }
        });
      };
      
      img.src = qrCodeUrl;
    } catch (err) {
      setError('Không thể copy vào clipboard');
    }
  };

  return (
    <ToolLayout 
      title="QR Code Generator" 
      description="Tạo QR codes từ text, URLs và data khác một cách dễ dàng"
    >
      <div className="input-group">
        <label className="input-label">Nhập text hoặc URL:</label>
        <textarea
          className="input-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={'Nhập text, URL, hoặc data của bạn ở đây...\n\nVí dụ:\n• https://example.com\n• Hello World!\n• Số điện thoại: +84 123 456 789\n• Email: user@example.com\n• WiFi: WIFI:T:WPA;S:MyNetwork;P:password;;'}
          rows={4}
        />
      </div>

      {/* QR Code Options */}
      <div className="input-group">
        <label className="input-label">🔧 Tùy chọn QR Code:</label>
        <div className="qr-options-grid">
          {/* Error Correction Level */}
          <div>
            <label className="option-label">Error Correction:</label>
            <select
              className="option-select"
              value={options.errorCorrectionLevel}
              onChange={(e) => setOptions({...options, errorCorrectionLevel: e.target.value as 'L' | 'M' | 'Q' | 'H'})}
            >
              <option value="L">Low (~7%)</option>
              <option value="M">Medium (~15%)</option>
              <option value="Q">Quartile (~25%)</option>
              <option value="H">High (~30%)</option>
            </select>
          </div>

          {/* Size */}
          <div>
            <label className="option-label">Size (px):</label>
            <input
              className="option-input"
              type="number"
              min="128"
              max="1024"
              value={options.width}
              onChange={(e) => setOptions({...options, width: parseInt(e.target.value)})}
            />
          </div>

          {/* Format */}
          <div>
            <label className="option-label">Format:</label>
            <select
              className="option-select"
              value={options.type}
              onChange={(e) => setOptions({...options, type: e.target.value as 'image/png' | 'image/jpeg'})}
            >
              <option value="image/png">PNG</option>
              <option value="image/jpeg">JPEG</option>
            </select>
          </div>

          {/* Margin */}
          <div>
            <label className="option-label">Margin:</label>
            <input
              className="option-input"
              type="number"
              min="0"
              max="10"
              value={options.margin}
              onChange={(e) => setOptions({...options, margin: parseInt(e.target.value)})}
            />
          </div>

          {/* Colors */}
          <div>
            <label className="option-label">Foreground:</label>
            <input
              className="option-color"
              type="color"
              value={options.color.dark}
              onChange={(e) => setOptions({...options, color: {...options.color, dark: e.target.value}})}
            />
          </div>

          <div>
            <label className="option-label">Background:</label>
            <input
              className="option-color"
              type="color"
              value={options.color.light}
              onChange={(e) => setOptions({...options, color: {...options.color, light: e.target.value}})}
            />
          </div>
        </div>
      </div>

      <div className="button-group">
        <button className="action-button" onClick={generateQR}>
          ⬛ Generate QR Code
        </button>
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {qrCodeUrl && (
        <div className="qr-result-section">
          <h3 className="qr-result-title">📱 QR Code Result:</h3>

          <div className="qr-image-container">
            <img
              src={qrCodeUrl}
              alt="Generated QR Code"
              className="qr-image"
            />
          </div>

          <div className="qr-actions">
            <button className="action-button" onClick={downloadQR}>
              💾 Download
            </button>
            <button className="action-button" onClick={copyToClipboard}>
              📋 Copy Image
            </button>
          </div>

          <div className="qr-hint">
            <strong>Scan với:</strong> Camera app, QR scanner apps, hoặc Google Lens
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="qr-info-section">
        <h4 className="qr-info-title">📱 QR Code Guide:</h4>
        <div className="qr-info-content">
          <p className="qr-info-item">
            <strong>📧 Email:</strong> mailto:user@example.com
          </p>
          <p className="qr-info-item">
            <strong>📞 Phone:</strong> tel:+84123456789
          </p>
          <p className="qr-info-item">
            <strong>📍 Location:</strong> geo:21.0285,105.8542
          </p>
          <p className="qr-info-item">
            <strong>📶 WiFi:</strong> WIFI:T:WPA;S:NetworkName;P:password;;
          </p>

          <strong>Error Correction Levels:</strong>
          <ul className="qr-info-list">
            <li><strong>Low (L):</strong> 7% - Tốt cho QR codes sạch</li>
            <li><strong>Medium (M):</strong> 15% - Cân bằng tốt (khuyến nghị)</li>
            <li><strong>Quartile (Q):</strong> 25% - Tốt cho môi trường bụi bẩn</li>
            <li><strong>High (H):</strong> 30% - Tốt nhất cho điều kiện khắc nghiệt</li>
          </ul>
        </div>
      </div>

      <style>{`
        .qr-options-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          padding: 1rem;
          background: var(--bg-secondary);
          border-radius: 10px;
          border: 2px solid var(--border-color);
        }

        .option-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          color: var(--text-primary);
        }

        .option-select,
        .option-input,
        .option-color {
          width: 100%;
          padding: 0.5rem;
          border-radius: 8px;
          border: 2px solid var(--border-color);
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        .option-select:focus,
        .option-input:focus,
        .option-color:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px var(--accent-light);
        }

        .option-color {
          height: 42px;
          cursor: pointer;
        }

        .qr-result-section {
          margin-top: 2rem;
          text-align: center;
        }

        .qr-result-title {
          color: var(--text-primary);
          margin-bottom: 1rem;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .qr-image-container {
          display: inline-block;
          padding: 2rem;
          background: var(--card-bg);
          border-radius: 12px;
          box-shadow: var(--shadow-md);
          border: 2px solid var(--border-color);
        }

        .qr-image {
          max-width: 100%;
          height: auto;
          display: block;
        }

        .qr-actions {
          margin-top: 1.5rem;
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .qr-hint {
          margin-top: 1rem;
          font-size: 0.875rem;
          color: var(--text-secondary);
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
        }

        .qr-hint strong {
          color: var(--text-primary);
        }

        .qr-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--accent-light);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .qr-info-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.125rem;
          font-weight: 700;
        }

        .qr-info-content {
          color: var(--text-primary);
          font-size: 0.875rem;
          line-height: 1.6;
        }

        .qr-info-item {
          margin: 0 0 0.5rem 0;
        }

        .qr-info-item strong {
          font-weight: 600;
        }

        .qr-info-list {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }

        .qr-info-list li {
          margin-bottom: 0.25rem;
        }

        @media (max-width: 768px) {
          .qr-options-grid {
            grid-template-columns: 1fr;
          }

          .qr-actions {
            flex-direction: column;
          }

          .qr-actions button {
            width: 100%;
          }
        }
      `}</style>
    </ToolLayout>
  );
};

export default QrGenerator;


